import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Download, Loader2, Rocket, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHead } from "@/components/brand/Card";
import { ConfirmDialog } from "@/components/admin/reference-data/dialogs";
import { ErrorBlock, StaleNotice } from "@/components/admin/reference-data/shared";
import { PackageSanityReport } from "@/components/admin/reference-data/PackageSanityReport";
import { UploadStatusBanner } from "@/components/admin/reference-data/UploadStatusBanner";
import {
  useConfigPackage,
  useIngestConfigType,
  usePromoteConfigPackage,
  useUploadConfigTypeFile,
} from "@/hooks/useApi";
import { downloadConfigTypeTemplate } from "@/api/reference-data";
import { configTypeUi, packageReleaseLabel } from "@/lib/reference-data-config-types";
import { errMessage } from "@/lib/reference-data-edit";
import { formatBytes } from "@/lib/datetime";
import type { ConfigTypeSummary } from "@/types/reference-data";

/** Upload do arquivo do tipo, polling do pacote e promote das tabelas de uma vez. */
export function PackageUploadPanel({
  configType,
  labelOf,
  file,
  setFile,
  packageId,
  setPackageId,
  uploading,
  setUploading,
  runError,
  setRunError,
}: {
  configType: ConfigTypeSummary;
  labelOf: (table: string) => string;
  file: File | null;
  setFile: (f: File | null) => void;
  packageId: string | undefined;
  setPackageId: (id: string | undefined) => void;
  /** Vive na página: este painel desmonta ao trocar de aba e o envio segue em andamento. */
  uploading: boolean;
  setUploading: (uploading: boolean) => void;
  /** Também na página: o envio pode falhar com outra aba aberta e o erro precisa sobreviver. */
  runError: string | null;
  setRunError: (message: string | null) => void;
}) {
  const { t } = useTranslation();
  const ui = configTypeUi(configType.key);
  // Lista estável: o hook de promote a usa para invalidar as tabelas do tipo.
  const tables = useMemo(() => configType.tables.map((x) => x.table_name), [configType.tables]);
  const upload = useUploadConfigTypeFile(configType.key);
  const ingest = useIngestConfigType(configType.key);
  const promote = usePromoteConfigPackage(configType.key, tables);
  // Só lê a consulta: quem invalida versões e auditoria quando o pacote sai de running é a página.
  const { data: pkg, isError: pkgError, refetch: refetchPkg } = useConfigPackage(
    configType.key,
    packageId,
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [options, setOptions] = useState<Record<string, string>>({});
  const missing = configType.upload_options.some((o) => !options[o.key]);
  // Enquanto o pacote não resolve (ou já está pronto para promover) outro envio duplicaria a carga.
  // Consulta que falhou sem nenhum dado não bloqueia: ela pode falhar para sempre.
  const packagePending = Boolean(
    packageId && ((!pkg && !pkgError) || pkg?.state === "running" || pkg?.state === "staged"),
  );
  const sending = uploading || upload.isPending || ingest.isPending || pkg?.state === "running";
  const busy = sending || packagePending;
  // Pacote em carga: trocar o arquivo zeraria o packageId e o polling, e a carga seguiria sem dono.
  // Entre o 202 e a primeira consulta também: soltar outro arquivo orfanaria a carga recém-criada.
  const awaitingFirstPoll = Boolean(packageId && !pkg && !pkgError);
  const dropDisabled = sending || awaitingFirstPoll;

  useEffect(() => {
    if (!file && fileInputRef.current) fileInputRef.current.value = "";
  }, [file]);

  // Avisa uma vez quando a carga que esta tela iniciou termina; pacote antigo, aberto de fora, não avisa.
  const notifiedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pkg || !packageId || pkg.package_id !== packageId) return;
    if (pkg.state !== "staged" && pkg.state !== "failed") return;
    const key = `${pkg.package_id}:${pkg.state}`;
    if (notifiedRef.current === key) return;
    notifiedRef.current = key;
    if (pkg.state === "staged") {
      toast.success(t("Upload finished successfully. Release {{release}} is ready to promote.", { release: packageReleaseLabel(pkg) }));
    } else {
      toast.error(pkg.failure_reason || t("Upload failed. Check the package details."));
    }
  }, [pkg, packageId, t]);

  const pickFile = (f: File | null) => {
    if (!f || dropDisabled) return;
    const name = f.name.toLowerCase();
    const accepted = ui.acceptedExtensions.some((ext) => name.endsWith(ext));
    setInvalid(!accepted);
    setRunError(null);
    if (!accepted) {
      toast.error(t(ui.wrongExtensionMessage));
      setFile(null);
      return;
    }
    setFile(f);
    setPackageId(undefined);
  };

  const handleRun = async () => {
    if (!file) return;
    setRunError(null);
    setUploading(true);
    let ingestStarted = false;
    try {
      const up = await upload.mutateAsync(file);
      toast.success(t("Uploaded & verified (SHA-256)."));
      ingestStarted = true;
      const started = await ingest.mutateAsync({
        object_key: up.object_key,
        filename: up.filename,
        expected_sha256: up.sha256,
        options: configType.upload_options.length > 0 ? options : undefined,
      });
      setPackageId(started.package_id);
    } catch (err) {
      let message = errMessage(err, t("Upload / ingest failed."));
      // Sem resposta (timeout/rede) ou 5xx no ingest: a carga pode ter começado mesmo assim.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (ingestStarted && (status === undefined || status >= 500)) {
        message = `${message} ${t("The load may still be running; check Releases before sending again.")}`;
      }
      setRunError(message);
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const downloadExample = async () => {
    try {
      const blob = await downloadConfigTypeTemplate(configType.key);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${configType.key}-exemplo.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(errMessage(err, t("Could not download the example.")));
    }
  };

  const handlePromote = async () => {
    if (!pkg) return;
    try {
      await promote.mutateAsync(pkg.package_id);
      toast.success(t("Release {{release}} promoted in every table.", { release: packageReleaseLabel(pkg) }));
      setPackageId(undefined);
      setFile(null);
    } catch (err) {
      toast.error(errMessage(err, t("Promotion failed.")));
    } finally {
      setConfirmOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHead
          overline={t("STEP 1 · UPLOAD")}
          title={t(ui.uploadTitle)}
          right={
            configType.has_template ? (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={downloadExample}>
                <Download className="w-3.5 h-3.5" />
                {t("Download example")}
              </Button>
            ) : undefined
          }
        />
        <div className="p-5 space-y-4">
          <p className="text-[12px] text-[#596b85]">
            {t(ui.uploadHelp)}
          </p>
          {configType.upload_options.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {configType.upload_options.map((o) => (
                <select
                  key={o.key}
                  aria-label={t(o.label_key)}
                  value={options[o.key] ?? ""}
                  onChange={(e) => setOptions((prev) => ({ ...prev, [o.key]: e.target.value }))}
                  className="h-9 rounded-md border border-[#e8ebf0] bg-white px-3 text-[13px] text-[#08254c]"
                >
                  <option value="">{t("Choose…")}</option>
                  {o.values.map((v) => (
                    <option key={v.value} value={v.value}>
                      {t(v.label_key)}
                    </option>
                  ))}
                </select>
              ))}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={ui.acceptedExtensions.join(",")}
            hidden
            disabled={dropDisabled}
            onChange={(e) => pickFile(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            aria-disabled={dropDisabled}
            onClick={() => {
              if (!dropDisabled) fileInputRef.current?.click();
            }}
            className="block w-full border-2 border-dashed border-[#e8ebf0] rounded-lg py-10 text-center cursor-pointer hover:border-[#ffc83d] hover:bg-[#fffdf5] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#08254c] focus-visible:ring-offset-2 aria-disabled:cursor-not-allowed aria-disabled:opacity-60 aria-disabled:hover:border-[#e8ebf0] aria-disabled:hover:bg-transparent"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              pickFile(e.dataTransfer.files?.[0] ?? null);
            }}
          >
            <UploadCloud className="w-8 h-8 mx-auto text-[#ffc83d] mb-3" />
            <span className="block text-[13px] text-[#08254c]">
              {file ? `${file.name} · ${formatBytes(file.size)}` : t(ui.dropLabel)}
            </span>
          </button>
          {upload.isPending && (
            <UploadStatusBanner tone="progress" message={t("Uploading and verifying the file…")} />
          )}
          {!upload.isPending && ingest.isPending && (
            <UploadStatusBanner tone="progress" message={t("Loading the file into the database…")} />
          )}
          {!upload.isPending && !ingest.isPending && pkg?.state === "running" && (
            <UploadStatusBanner tone="progress" message={t("Load in progress…")} />
          )}
          {!sending && pkg?.state === "staged" && (
            <UploadStatusBanner
              tone="success"
              message={t(
                "Upload completed successfully. Release {{release}} is ready; review the package below before promoting.",
                { release: packageReleaseLabel(pkg) },
              )}
            />
          )}
          {!sending && pkg?.state === "failed" && (
            <UploadStatusBanner tone="error" message={t("Upload failed. Check the package details.")} />
          )}
          {uploading && (
            <p role="status" className="text-[12px] text-[#596b85]">
              {t("Wait for the current upload to finish.")}
            </p>
          )}
          <Button
            variant="action"
            className="w-full gap-1.5"
            onClick={handleRun}
            disabled={!file || invalid || busy || missing}
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
            {t("Upload & stage")}
          </Button>
          {pkg?.state === "staged" && !sending && (
            <p role="status" className="text-[12px] text-[#596b85]">
              {t("Promote the release, roll back in Releases or choose another file before uploading another spreadsheet.")}
            </p>
          )}
          {runError && <ErrorBlock message={runError} />}
          {packageId && pkgError && !pkg && (
            <>
              <ErrorBlock
                message={t("Could not load the package status.")}
                onRetry={() => void refetchPkg()}
              />
              {/* O backend não é idempotente: outro envio carregaria a mesma planilha de novo. */}
              <p role="status" className="text-[12px] text-[#b57a00]">
                {t("The load may still be running; check Releases before sending again.")}
              </p>
            </>
          )}
          {packageId && pkgError && pkg && <StaleNotice />}
        </div>
      </Card>

      {pkg && pkg.state !== "running" && (
        <Card>
          <CardHead
            overline={t("STEP 2 · REVIEW")}
            title={t("Package")}
            right={
              pkg.state === "staged" ? (
                <Button
                  variant="action"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => setConfirmOpen(true)}
                  disabled={promote.isPending}
                >
                  <Rocket className="w-3.5 h-3.5" />
                  {t("Promote release {{release}}", { release: packageReleaseLabel(pkg) })}
                </Button>
              ) : undefined
            }
          />
          <div className="p-5">
            <PackageSanityReport pkg={pkg} labelOf={labelOf} />
          </div>
        </Card>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t("Promote release")}
        description={t(
          "Promote this release in every table of the configuration type? The agents read it immediately.",
        )}
        confirmLabel={t("Promote")}
        confirmIcon={<Rocket className="w-3.5 h-3.5" />}
        onConfirm={handlePromote}
        loading={promote.isPending}
      />
    </div>
  );
}
